import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Search, 
  Eye, 
  Shield, 
  Activity, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  FileText,
  User,
  Server,
  Calendar,
  FileCheck2
} from 'lucide-react';
import { toast } from 'sonner';

const auditTrailData = [
  {
    id: 'AUD-2024-5678',
    timestamp: '2025-10-24 14:32:15',
    action: 'Invoice Processed',
    entityType: 'Sales Invoice',
    entityId: 'INV-2024-1247',
    user: 'system@abctrading.ae',
    ipAddress: '172.16.0.45',
    status: 'success',
    details: {
      amount: 15240,
      vendor: 'Acme Corporation',
      serviceProvider: 'ServicePro UAE',
      ftaRef: 'FTA-2024-XYZ1247'
    },
    changes: [
      { field: 'Status', oldValue: 'pending', newValue: 'processed' },
      { field: 'FTA Reference', oldValue: null, newValue: 'FTA-2024-XYZ1247' }
    ]
  },
  {
    id: 'AUD-2024-5677',
    timestamp: '2025-10-24 14:15:22',
    action: 'API Call',
    entityType: 'Service Provider',
    entityId: 'SP-ServicePro',
    user: 'api@abctrading.ae',
    ipAddress: '172.16.0.45',
    status: 'success',
    details: {
      endpoint: '/invoices/sales',
      method: 'POST',
      responseTime: '245ms',
      statusCode: 200
    },
    changes: []
  },
  {
    id: 'AUD-2024-5676',
    timestamp: '2025-10-24 13:45:10',
    action: 'Invoice Validation Failed',
    entityType: 'Sales Invoice',
    entityId: 'INV-2024-1246',
    user: 'system@abctrading.ae',
    ipAddress: '172.16.0.45',
    status: 'failed',
    details: {
      amount: 22500,
      vendor: 'Tech Ventures LLC',
      errorCode: 'VAL_001',
      errorMessage: 'Invalid tax number format'
    },
    changes: []
  },
  {
    id: 'AUD-2024-5675',
    timestamp: '2025-10-24 12:30:45',
    action: 'User Login',
    entityType: 'User',
    entityId: 'USR-001',
    user: 'admin@abctrading.ae',
    ipAddress: '10.0.1.25',
    status: 'success',
    details: {
      loginMethod: 'Password',
      sessionId: 'sess_xyz789'
    },
    changes: []
  },
  {
    id: 'AUD-2024-5674',
    timestamp: '2025-10-24 11:22:33',
    action: 'Configuration Updated',
    entityType: 'Service Provider',
    entityId: 'SP-ServicePro',
    user: 'admin@abctrading.ae',
    ipAddress: '10.0.1.25',
    status: 'success',
    details: {
      configType: 'API Endpoint',
      module: 'Sales Invoice'
    },
    changes: [
      { field: 'Endpoint URL', oldValue: '/v1/invoices/sales', newValue: '/v2/invoices/sales' }
    ]
  }
];

const apiMonitoringData = [
  {
    timestamp: '2025-10-24 14:32:15',
    endpoint: '/api/v2/process',
    method: 'POST',
    statusCode: 200,
    responseTime: 245,
    invoiceId: 'INV-2024-1247',
    serviceProvider: 'ServicePro UAE',
    erpSource: 'SAP'
  },
  {
    timestamp: '2025-10-24 14:15:22',
    endpoint: '/api/v2/process',
    method: 'POST',
    statusCode: 200,
    responseTime: 198,
    invoiceId: 'INV-2024-1246',
    serviceProvider: 'ServicePro UAE',
    erpSource: 'SAP'
  },
  {
    timestamp: '2025-10-24 13:45:10',
    endpoint: '/api/v2/process',
    method: 'POST',
    statusCode: 400,
    responseTime: 156,
    invoiceId: 'INV-2024-1245',
    serviceProvider: 'ServicePro UAE',
    erpSource: 'SAP'
  },
  {
    timestamp: '2025-10-24 12:30:45',
    endpoint: '/api/v2/validate',
    method: 'POST',
    statusCode: 200,
    responseTime: 89,
    invoiceId: 'INV-2024-1244',
    serviceProvider: 'ServicePro UAE',
    erpSource: 'SAP'
  }
];

const dataRetentionData = [
  { category: 'Sales Invoices', count: 12456, size: '2.3 GB', retention: '7 years', compliance: 'FTA UAE' },
  { category: 'Purchase Invoices', count: 8923, size: '1.7 GB', retention: '7 years', compliance: 'FTA UAE' },
  { category: 'Credit Notes', count: 345, size: '124 MB', retention: '7 years', compliance: 'FTA UAE' },
  { category: 'API Logs', count: 156789, size: '892 MB', retention: '1 year', compliance: 'Internal' },
  { category: 'Audit Trails', count: 45678, size: '345 MB', retention: 'Indefinite', compliance: 'ISO 27001' }
];

export default function AuditMonitoring() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAction, setFilterAction] = useState('all');

  const filteredAuditTrail = auditTrailData.filter(log => {
    const matchesSearch = log.entityId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.action.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    return matchesSearch && matchesStatus && matchesAction;
  });

  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setIsViewerOpen(true);
  };

  const handleExport = () => {
    toast.success('Exporting audit trail...');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-gray-900">Audit & Monitoring</h1>
          <p className="text-gray-600 mt-1">Comprehensive audit trails and system monitoring (UAE Standard)</p>
        </div>
        <Button className="bg-[#0052CC]" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Audit Log
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Events (24h)</p>
                <p className="text-2xl text-gray-900 mt-2">1,247</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-2xl text-green-600 mt-2">98.5%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed Events</p>
                <p className="text-2xl text-red-600 mt-2">19</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Response Time</p>
                <p className="text-2xl text-gray-900 mt-2">195ms</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="audit-trail" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="audit-trail">Audit Trail</TabsTrigger>
          <TabsTrigger value="api-monitoring">API Monitoring</TabsTrigger>
          <TabsTrigger value="data-retention">Data Retention</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        {/* Audit Trail Tab */}
        <TabsContent value="audit-trail" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search by invoice ID, user, or action..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterAction} onValueChange={setFilterAction}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="Invoice Processed">Invoice Processed</SelectItem>
                    <SelectItem value="API Call">API Call</SelectItem>
                    <SelectItem value="User Login">User Login</SelectItem>
                    <SelectItem value="Configuration Updated">Configuration Updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Audit Trail Table */}
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail Log ({filteredAuditTrail.length} records)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity ID</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAuditTrail.map((log) => (
                    <TableRow key={log.id} className="cursor-pointer hover:bg-gray-50">
                      <TableCell className="text-sm">{log.timestamp}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.action}</Badge>
                      </TableCell>
                      <TableCell>{log.entityType}</TableCell>
                      <TableCell className="text-[#0052CC]">{log.entityId}</TableCell>
                      <TableCell className="text-sm">{log.user}</TableCell>
                      <TableCell className="text-sm text-gray-600">{log.ipAddress}</TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(log)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Monitoring Tab */}
        <TabsContent value="api-monitoring" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Real-time API Monitoring</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Invoice ID</TableHead>
                    <TableHead>Service Provider</TableHead>
                    <TableHead>ERP Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Response Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiMonitoringData.map((api, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-sm">{api.timestamp}</TableCell>
                      <TableCell className="text-sm text-gray-700">{api.endpoint}</TableCell>
                      <TableCell>
                        <Badge variant={api.method === 'POST' ? 'default' : 'secondary'}>
                          {api.method}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#0052CC]">{api.invoiceId}</TableCell>
                      <TableCell>{api.serviceProvider}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{api.erpSource}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={api.statusCode === 200 ? 'bg-green-500' : 'bg-red-500'}>
                          {api.statusCode}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm ${api.responseTime < 300 ? 'text-green-600' : 'text-yellow-600'}`}>
                          {api.responseTime}ms
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <Server className="w-8 h-8 text-blue-500 mb-3" />
                <p className="text-sm text-gray-600">Total API Calls (24h)</p>
                <p className="text-2xl text-gray-900 mt-2">3,456</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-sm text-gray-600">Successful Calls</p>
                <p className="text-2xl text-green-600 mt-2">3,402</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
                <p className="text-sm text-gray-600">Failed Calls</p>
                <p className="text-2xl text-red-600 mt-2">54</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data Retention Tab */}
        <TabsContent value="data-retention" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Data Retention Policy - UAE Compliance</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data Category</TableHead>
                    <TableHead>Record Count</TableHead>
                    <TableHead>Storage Size</TableHead>
                    <TableHead>Retention Period</TableHead>
                    <TableHead>Compliance Standard</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataRetentionData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-600" />
                          <span>{item.category}</span>
                        </div>
                      </TableCell>
                      <TableCell>{item.count.toLocaleString()}</TableCell>
                      <TableCell>{item.size}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.retention}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-500">{item.compliance}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>UAE FTA Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900">Tax Invoices</p>
                    <p className="text-sm text-gray-600">Must be retained for 7 years from end of tax period</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900">Accounting Records</p>
                    <p className="text-sm text-gray-600">All records must be in Arabic or English</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900">Electronic Storage</p>
                    <p className="text-sm text-gray-600">Records must be readable and accessible on demand</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900">Audit Trail</p>
                    <p className="text-sm text-gray-600">Complete transaction history must be maintained</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Storage Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Data Stored</p>
                  <p className="text-2xl text-gray-900 mt-1">5.4 GB</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Records</p>
                  <p className="text-2xl text-gray-900 mt-1">224,537</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Oldest Record</p>
                  <p className="text-2xl text-gray-900 mt-1">7 years ago</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>FTA Compliance Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Shield className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-gray-900">VAT Registration</p>
                      <p className="text-sm text-gray-600">TRN: AE123456789</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileCheck2 className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-gray-900">Tax Period Filing</p>
                      <p className="text-sm text-gray-600">Q3 2025 Filed on Time</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500">Compliant</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-gray-900">Record Retention</p>
                      <p className="text-sm text-gray-600">7 years maintained</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500">Compliant</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-gray-900">Electronic Invoicing</p>
                      <p className="text-sm text-gray-600">Phase 2 Compliant</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500">Compliant</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ISO 27001 Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Shield className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-gray-900">Data Encryption</p>
                      <p className="text-sm text-gray-600">AES-256 at rest & transit</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-500">Enabled</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <User className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-gray-900">Access Control</p>
                      <p className="text-sm text-gray-600">Role-based permissions</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-500">Active</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Activity className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-gray-900">Audit Logging</p>
                      <p className="text-sm text-gray-600">All events tracked</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-500">Active</Badge>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Server className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-gray-900">Backup & Recovery</p>
                      <p className="text-sm text-gray-600">Daily automated backups</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-500">Active</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Audit Detail Dialog */}
      <Dialog open={isViewerOpen} onOpenChange={setIsViewerOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Audit Log Details - {selectedLog?.id}</DialogTitle>
            <DialogDescription>Complete audit trail information and change history</DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Event Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Timestamp</p>
                      <p className="text-gray-900">{selectedLog.timestamp}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Action</p>
                      <Badge variant="outline">{selectedLog.action}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      {getStatusBadge(selectedLog.status)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">User</p>
                      <p className="text-gray-900">{selectedLog.user}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">IP Address</p>
                      <p className="text-gray-900">{selectedLog.ipAddress}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Entity Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Entity Type</p>
                      <p className="text-gray-900">{selectedLog.entityType}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Entity ID</p>
                      <p className="text-[#0052CC]">{selectedLog.entityId}</p>
                    </div>
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-sm text-gray-600">{key}</p>
                        <p className="text-gray-900">{String(value)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {selectedLog.changes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Changes Made</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Field</TableHead>
                          <TableHead>Old Value</TableHead>
                          <TableHead>New Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedLog.changes.map((change: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{change.field}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{change.oldValue || 'null'}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-500">{change.newValue}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
